/**
 * @fileoverview User profile dropdown menu with quick links.
 *
 * @purpose Show profile info and navigation actions (profile, settings, help, logout).
 * @usage Rendered by the TopBar when the avatar menu is opened.
 * @ownership Shared UI components (core)
 * @dependencies
 * - @/core/components/shared/Icon/ModuleIcon
 * - @/core/components/shared/UserAvatarBubble
 * - @korvo/core/hooks/useFocusTrap
 * - @korvo/core/hooks/useKeyboard
 * @restrictions
 * - DO NOT render without focus trapping when open.
 */
'use client';

import { useFocusTrap, useKeyboard } from '@korvo/core';
import { createPortal } from 'react-dom';

import { ModuleIcon } from '../Icon/ModuleIcon';
import { UserAvatarBubble } from '../UserAvatarBubble';
import { type UserProfile } from './types';

type UserProfileMenuProps = {
  isOpen: boolean;
  onClose: () => void;
  userProfile: UserProfile;
  onLogout: () => void;
  isMobile: boolean;
}

export function UserProfileMenu({
  isOpen,
  onClose,
  userProfile,
  onLogout,
  isMobile,
}: UserProfileMenuProps) {
  // Mobile: full-width with safe insets so popup is never cut off. Desktop: anchored top-right.
  const menuClass = isMobile
    ? 'fixed rounded-lg shadow-lg'
    : 'fixed right-4 top-16 rounded-lg shadow-lg';

  const menuStyle: React.CSSProperties = {
    backgroundColor: 'var(--background-secondary)',
    color: 'var(--foreground-default)',
  };
  if (isMobile) {
    menuStyle.left = 'max(1rem, env(safe-area-inset-left, 0px))';
    menuStyle.right = 'max(1rem, env(safe-area-inset-right, 0px))';
    menuStyle.top = 'calc(var(--topbar-height, 4rem) + 0.25rem)';
    menuStyle.width = 'auto';
    menuStyle.maxWidth = 'calc(100vw - 2rem)';
    menuStyle.maxHeight = 'calc(100dvh - var(--topbar-height, 4rem) - 1rem)';
    menuStyle.overflowY = 'auto';
  } else {
    menuStyle.width = '360px';
    menuStyle.maxWidth = 'calc(100vw - 2rem)';
    menuStyle.right = 'max(1rem, env(safe-area-inset-right, 0px))';
  }

  const buttonClass = isMobile
    ? 'w-full text-left px-3 py-3 min-h-[44px] rounded flex items-center transition-colors touch-manipulation'
    : 'w-full text-left px-3 py-2 rounded flex items-center transition-colors';

  // Handle keyboard navigation
  useKeyboard({ onEscape: onClose }, isOpen);
  const containerRef = useFocusTrap(isOpen);

  const popup = (
    <>
      <div
        className="fixed inset-0 z-[9998]"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        ref={containerRef}
        className={`${menuClass} z-[9999] transition-all duration-200 origin-top-right scale-100 opacity-100`}
        style={menuStyle}
        role="dialog"
        aria-modal="true"
        aria-label="User menu"
      >
        <div
          className="p-4 border-b shrink-0"
          style={{ borderColor: 'var(--border-default)' }}
        >
          <div className="flex items-center gap-3 min-w-0">
            <UserAvatarBubble
              name={userProfile.name}
              email={userProfile.email}
              avatarUrl={userProfile.avatar}
              size={40}
            />
            <div className="min-w-0 flex-1">
              <div className="font-medium truncate" style={{ color: 'var(--foreground-default)' }}>{userProfile.name}</div>
              <div className="text-sm truncate" style={{ color: 'var(--foreground-muted)' }}>{userProfile.email}</div>
            </div>
          </div>
        </div>

        <div className="p-2">
          <button
            className={buttonClass}
            style={{
              color: 'var(--foreground-default)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--panel-hover)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
            onClick={() => {
              window.location.href = '/profile';
              if (isMobile) onClose();
            }}
          >
            <ModuleIcon icon="ri-user-line" className="h-4 w-4 mr-3" />
            <span>Profile</span>
          </button>
          
          <button
            className={buttonClass}
            style={{
              color: 'var(--foreground-default)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--panel-hover)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
            onClick={() => {
              window.location.href = '/settings';
              if (isMobile) onClose();
            }}
          >
            <ModuleIcon icon="ri-settings-3-line" className="h-4 w-4 mr-3" />
            <span>Settings</span>
          </button>
          
          <button
            className={buttonClass}
            style={{
              color: 'var(--foreground-default)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--panel-hover)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
            onClick={() => {
              window.location.href = '/help';
              if (isMobile) onClose();
            }}
          >
            <ModuleIcon icon="ri-question-line" className="h-4 w-4 mr-3" />
            <span>Help</span>
          </button>

          <div className="my-2 border-t shrink-0" style={{ borderColor: 'var(--border-default)' }} />
          
          <button
            className={buttonClass}
            style={{
              backgroundColor: 'var(--danger-default)',
              color: 'var(--danger-foreground)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--panel-hover)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--danger-default)';
            }}
            onClick={() => {
              onLogout();
              if (isMobile) onClose();
            }}
          >
            <ModuleIcon icon="ri-logout-box-line" className="h-4 w-4 mr-3" />
            <span>Logout</span>
          </button>
        </div>
      </div>
    </>
  );

  // Portal to body so fixed positioning is viewport-relative and never clipped by parent overflow/transform
  if (!isOpen) return null;
  if (typeof document === 'undefined') return null;
  return createPortal(popup, document.body);
}